import { generateRankMathTags } from "helpers/generateRankMathTags";
import Head from "next/head";

interface ISEOProps {
	url?: string;
	uri?: string;
	canonicalPage?: string;
	title?: string;
	image?: string;
	readTime?: string;
	pageType?: string;
	createdAt?: string;
	authorName?: string;
	modifiedAt?: string;
	description?: string;
	shouldIndexPage?: boolean;
	shouldExcludeTitleSuffix?: boolean;
	structuredData?: Record<string, unknown>;
	rankMathData?: string;
}

export default function SEO({
	url,
	title,
	description,
	image,
	readTime,
	createdAt,
	modifiedAt,
	authorName,
	pageType = "website",
	shouldExcludeTitleSuffix = false,
	shouldIndexPage = true,
	structuredData,
	canonicalPage,
	rankMathData,
}: ISEOProps): JSX.Element {
	const pageTitle = `${title} ${!shouldExcludeTitleSuffix ? "| Amigo Edu | Blog" : ""}`;
	const pageImage = image ? `${process.env.NEXT_PUBLIC_SITE_URL}` : null;

	return (
		<Head>
			<meta name="viewport" content="initial-scale=1.0, width=device-width" />

			{!["article", "category"].includes(pageType) && (
				<>
					{structuredData && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />}

					{description && <meta name="description" content={description} />}
					{image && <meta name="image" content={pageImage} />}
					{pageType === "category" && <link rel="canonical" href={canonicalPage} />}

					{!["article", "category"].includes(pageType) && shouldIndexPage ? (
						<meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
					) : (
						<meta name="robots" content="noindex,nofollow" />
					)}
				</>
			)}

			<meta httpEquiv="x-ua-compatible" content="IE=edge,chrome=1" />
			<meta name="MobileOptimized" content="320" />
			<meta name="HandheldFriendly" content="True" />
			<meta name="theme-color" content="##3864b2" />
			<meta name="referrer" content="no-referrer-when-downgrade" />
			<meta name="google" content="notranslate" />
			<meta name="msapplication-TileColor" content="#000000" />
			<meta name="msapplication-config" content="public/blog_static/favicon/browserconfig.xml" />
			<link rel="apple-touch-icon" sizes="180x180" href="public/blog_static/favicon/apple-touch-icon.png" />
			<link rel="icon" type="image/png" sizes="32x32" href="public/blog_static/favicon/favicon-32x32.png" />
			<link rel="icon" type="image/png" sizes="16x16" href="public/blog_static/favicon/favicon-16x16.png" />
			<link rel="mask-icon" href="public/blog_static/favicon/safari-pinned-tab.svg" color="#000000" />
			<link rel="shortcut icon" href="/public/blog_static/favicon.ico" />

			{["article", "category"].includes(pageType) && generateRankMathTags(rankMathData)}

			{!["article", "category"].includes(pageType) && (
				<>
					<title>{pageTitle}</title>
					<link rel="canonical" href={canonicalPage} />
					<meta property="article:publisher" content="https://www.facebook.com/amigoeduoficial/" />
					<meta property="article:published_time" content={createdAt} />
					<meta property="article:modified_time" content={modifiedAt} />
					<meta property="og:title" content={`${pageTitle}`} />
					<meta property="og:description" content={description} />
					<meta property="og:locale" content="pt_BR" />
					<meta property="og:type" content={pageType} />
					<meta property="og:url" content={url} />
					<meta property="og:site_name" content="Blog do Amigo Edu" />
					<meta property="og:image" content={pageImage} />
					<meta property="og:image:secure_url" content={pageImage} />
					<meta property="og:image:alt" content="Thumbnail" />
					<meta property="og:image:type" content="image/png" />
					<meta property="og:image:width" content="1920" />
					<meta property="og:image:height" content="500" />
					<meta name="twitter:site" content="@amigoeduoficial" />
					<meta name="twitter:card" content="summary_large_image" />
					<meta name="twitter:creator" content="@amigoeduoficial" />
				</>
			)}
		</Head>
	);
}
